import { useUser } from "canbook-data/user/user-provider";
import { ProfileComponentCallback } from "./hooks";
import { useState } from "react";
import { z } from "zod";
import {
  INVALID_EMAIL,
  INVALID_PHONE,
  REQUIRED_ERROR_NAME,
  REQUIRED_ERROR_PHONE,
  VALIDATION_ERROR_MIN_NAME_LENGTH,
  VALIDATION_ERROR_PHONE,
} from "@/utils/const";
import { r2Service } from "canbook-data/r2/service";
import { R2Module } from "canbook-data/r2/model";
import { DbUser } from "canbook-data/user/models";
import { authService } from "canbook-data/auth/service";
import {
  DEFAULT_DIAL_CODE,
  COUNTRY_CODES,
} from "canbook-data/util/country-codes";
import { userService } from "canbook-data/user/service";

export const useProfileState = (
  userId: string,
  phone: string,
  email: string,
  callback: ProfileComponentCallback,
) => {
  const { user } = useUser();

  const [apiError, setApiError] = useState(null);
  const [name, setName] = useState<string>("");
  const [emailState, setEmailState] = useState<string>(
    user && user.email ? user.email : email || "",
  );

  const initialFullPhone = (user && user.phone) || phone || "";

  let initialCountryCode = DEFAULT_DIAL_CODE;
  let initialPhoneDigits = "";

  if (initialFullPhone && initialFullPhone.startsWith("+")) {
    const normalized = initialFullPhone.replace(/\s/g, "");
    const match = normalized.match(/^(\+\d{1,4})(\d{4,})$/);
    if (match) {
      initialCountryCode = match[1];
      initialPhoneDigits = match[2];
    }
  } else if (initialFullPhone) {
    initialPhoneDigits = initialFullPhone.replace(/\D/g, "");
  }

  const [phoneState, setPhoneState] = useState<string>(initialPhoneDigits);
  const [countryCode, setCountryCode] = useState<string>(initialCountryCode);
  const [profileUrl, setProfileUrl] = useState<string | null>(
    user && user.profile_image_url ? user.profile_image_url : null,
  );

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageData, setImageData] = useState<any | null>(null);

  const [errors, setErrors] = useState<any[]>([]);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);

  // Check if this is an email login (email exists but phone doesn't)
  const isEmailLogin = !!email && !phone;

  const checkValidation = (
    phoneToValidate?: string,
    countryCodeToValidate?: string,
  ) => {
    const country = COUNTRY_CODES.find(
      (c) => c.dialCode === (countryCodeToValidate ?? countryCode),
    );
    const minLength = country?.minLength || 1;
    const maxLength = country?.maxLength || 15;

    const profileSchema = z.object({
      name: z
        .string({ required_error: REQUIRED_ERROR_NAME })
        .min(3, { message: VALIDATION_ERROR_MIN_NAME_LENGTH }),
      phone: z
        .string({ required_error: REQUIRED_ERROR_PHONE })
        .min(minLength, { message: VALIDATION_ERROR_PHONE })
        .max(maxLength, { message: VALIDATION_ERROR_PHONE })
        .refine((val) => /^\d+$/.test(val), {
          message: INVALID_PHONE,
        }),
      email: z
        .string()
        .optional()
        .refine((val) => !val || z.string().email().safeParse(val).success, {
          message: INVALID_EMAIL,
        }),
    });

    const response = profileSchema.safeParse({
      name: name.trim(),
      phone: (phoneToValidate ?? phoneState).replace(/\D/g, ""),
      email: emailState,
    });

    if (!response.success) {
      const errArr: any[] = [];
      const { errors: err } = response.error;
      for (let i = 0; i < err.length; i++) {
        errArr.push({ for: err[i].path[0], message: err[i].message });
      }
      setErrors(errArr);
      setIsInProgress(false);
      return errArr;
    }

    setErrors([]);
    return [];
  };

  const handleProfileSubmit = async (e: any) => {
    e.preventDefault();
    setIsInProgress(true);

    const errors = checkValidation();
    if (errors.length > 0) return;

    try {
      let image_url = profileUrl;

      if (imageData) {
        try {
          image_url = await r2Service.upload(imageData, R2Module.user);
        } catch (error: any) {
          setApiError(error);
          return;
        }
      }

      if (
        (user?.profile_image_url && !profileUrl) ||
        (user?.profile_image_url && imageData)
      ) {
        await r2Service.deleteFile(
          R2Module.user,
          user?.profile_image_url as string,
        );
      }

      if (user) {
        userId ||= user?.id;
      }

      const phoneDigits = phoneState.replace(/\D/g, "");
      const phoneFull = `${countryCode}${phoneDigits}`;
      const userData: DbUser = {
        id: userId,
        user_name: name.trim(),
        email: emailState,
        phone: phoneFull,
        country_code: countryCode,
        profile_image_url: image_url,
      };

      const updatedUser = await authService.updateUserProfile(userData);

      if (isEmailLogin) {
        try {
          await userService.updateUserPhone(updatedUser.id, phoneFull);
        } catch (error: any) {
          const isPhoneTaken =
            error?.response?.data === "phone-already-exists" ||
            error?.response?.data?.error === "phone-already-exists";
          const apiErrorMessage = isPhoneTaken
            ? "This phone number is already registered. Please try another number."
            : error;
          setApiError(apiErrorMessage);
          return;
        }
      }

      callback.onSuccess(updatedUser);
    } catch (error: any) {
      setApiError(error);
      return;
    } finally {
      setIsInProgress(false);
    }
  };

  const handleFileUploading = async (file: any) => {
    setImageData(file);
    setIsImageModalOpen(false);
  };

  const removeProfileImage = async () => {
    setProfileUrl(null);
    setIsImageModalOpen(false);
  };

  return {
    user,
    apiError,
    name,
    email: emailState,
    phone: phoneState,
    countryCode,
    profileUrl,
    errors,
    isInProgress,
    isEmailLogin,
    setErrors,
    setApiError,
    setName,
    setEmail: setEmailState,
    setPhone: setPhoneState,
    setCountryCode,
    setIsImageModalOpen,
    isImageModalOpen,
    handleProfileSubmit,
    handleFileUploading,
    removeProfileImage,
    checkValidation,
    maxPhoneLength:
      COUNTRY_CODES.find((c) => c.dialCode === countryCode)?.maxLength || 15,
  };
};
